import { readBlob2At } from "./blob_helpers";


export const STICKERINFOGPU_STRIDE_BYTES = 472;
export const STICKERINFOGPU_STRIDE_WORDS = 118;
const COLOR_INDEX_BLUE =  0;
const COLOR_INDEX_GREEN = 1;
const COLOR_INDEX_RED = 2;

export type DetectedCircleInfoGPU = {
  x: number;
  y: number;
  w: number;
  h: number;
  v: number;
  cb: number;
  cr: number;
  pad: number;
};

export type StickerInfoGpu = {
  index: number;
  byteOffset: number;
  strideBytes: number;
  strideWords: number;

  code: bigint;
  xtypex: number;
  debug_stage: number;

  blob_list: number[];
  circle_colors: number[];
  circle_spins: number[];
  detected_circle_info: DetectedCircleInfoGPU[];

  sticker_direction: [number, number];
  debug_fail_blob: number;
  pad0: number;
};

function stickerDv(words: Uint32Array): DataView {
  return new DataView(words.buffer, words.byteOffset, words.byteLength);
}

function i32At(dv: DataView, absByteOffset: number): number {
  return dv.getInt32(absByteOffset, true);
}

function u32At(dv: DataView, absByteOffset: number): number {
  return dv.getUint32(absByteOffset, true);
}

function f32At(dv: DataView, absByteOffset: number): number {
  return dv.getFloat32(absByteOffset, true);
}

function u64At(dv: DataView, absByteOffset: number): bigint {
  return dv.getBigUint64(absByteOffset, true);
}

function readDetectedCircleInfoGPU(
  dv: DataView,
  base: number
): DetectedCircleInfoGPU {
  return {
    x: i32At(dv, base + 0),
    y: i32At(dv, base + 4),
    w: i32At(dv, base + 8),
    h: i32At(dv, base + 12),
    v: i32At(dv, base + 16),
    cb: i32At(dv, base + 20),
    cr: i32At(dv, base + 24),
    pad: i32At(dv, base + 28),
  };
}

function readI32Array(
  dv: DataView,
  base: number,
  count: number
): number[] {
  const out = new Array<number>(count);
  for (let i = 0; i < count; i++) {
    out[i] = i32At(dv, base + i * 4);
  }
  return out;
}

function readDetectedCircleInfoArray(
  dv: DataView,
  base: number,
  count: number
): DetectedCircleInfoGPU[] {
  const out = new Array<DetectedCircleInfoGPU>(count);
  for (let i = 0; i < count; i++) {
    out[i] = readDetectedCircleInfoGPU(dv, base + i * 32);
  }
  return out;
}

export function readStickerAt(words: Uint32Array, index: number): StickerInfoGpu {
  const dv = stickerDv(words);
  const base = index * STICKERINFOGPU_STRIDE_BYTES;

  return {
    index,
    byteOffset: base,
    strideBytes: STICKERINFOGPU_STRIDE_BYTES,
    strideWords: STICKERINFOGPU_STRIDE_WORDS,

    code: u64At(dv, base + 0),
    xtypex: u32At(dv, base + 8),
    debug_stage: u32At(dv, base + 12),

    blob_list: readI32Array(dv, base + 16, 10),
    circle_colors: readI32Array(dv, base + 56, 10),
    circle_spins: readI32Array(dv, base + 96, 10),
    detected_circle_info: readDetectedCircleInfoArray(dv, base + 136, 10),

    sticker_direction: [
      f32At(dv, base + 456),
      f32At(dv, base + 460),
    ],
    debug_fail_blob: i32At(dv, base + 464),
    pad0: i32At(dv, base + 468),
  };
}

export function printSticker(words: Uint32Array, index: number): void {
  const s = readStickerAt(words, index);
  console.log(`StickerInfoGpu[${index}]`, JSON.stringify(s, bigintReplacer, 2));
}

export function printStickerRange(
  words: Uint32Array,
  start: number,
  count: number
): void {
  for (let i = 0; i < count; i++) {
    printSticker(words, start + i);
  }
}

export function findUsedStickerIndices(words: Uint32Array): number[] {
  const stickerCount = Math.floor(words.byteLength / STICKERINFOGPU_STRIDE_BYTES);
  const out: number[] = [];

  for (let i = 0; i < stickerCount; i++) {
    const s = readStickerAt(words, i);

    const hasCode = s.code !== 0n;
    const hasBlob = s.blob_list.some(v => v >= 0);
    const hasCircle = s.detected_circle_info.some(c => c.w > 0 || c.h > 0);
    const hasDebug = s.debug_stage !== 0 || s.debug_fail_blob !== 0;

    if (hasCode || hasBlob || hasCircle || hasDebug) {
      out.push(i);
    }
  }

  console.log("usedStickerIndices =", out);
  return out;
}

export function listStickerSummaries(words: Uint32Array) {
  const indices = findUsedStickerIndices(words);

  const rows = indices.map((index) => {
    const s = readStickerAt(words, index);
    return {
      index: s.index,
      code: s.code.toString(),
      xtypex: s.xtypex,
      debug_stage: s.debug_stage,
      debug_fail_blob: s.debug_fail_blob,
      blob_list: s.blob_list.filter(v => v >= 0),
      circle_colors: s.circle_colors.filter(v => v >= 0),
      circle_spins: s.circle_spins.filter(v => v !== 0),
      sticker_direction: s.sticker_direction,
    };
  });

  console.log(`StickerInfoGpu[${rows.length}]`, JSON.stringify(rows, null, 2));
  return rows;
}
export function colorName(colorIndex: number): string {
  switch (colorIndex) {
    case 0: return "blue";
    case 1: return "green";
    case 2: return "red";
    case 3: return "black";
    case -1: return "unknown";
    default: return `color${colorIndex}`;
  }
}

export function listStickerSummariesPretty(words: Uint32Array) {
  const indices = findUsedStickerIndices(words);

  const rows = indices.map((index) => {
    const s = readStickerAt(words, index);

    const blobs = s.blob_list.filter(v => v >= 0);
    const colors = s.circle_colors
      .filter(v => v >= 0)
      .map(v => `${colorName(v)} (${v})`);

    return {
      index: s.index,
      code: s.code.toString(),
      xtypex: s.xtypex,
      debug_stage: s.debug_stage,
      debug_fail_blob: s.debug_fail_blob,
      blobs,
      colors,
      sticker_direction: s.sticker_direction,
    };
  });

  console.log(`StickerInfoGpu[${rows.length}]`, JSON.stringify(rows, null, 2));
  return rows;
}

function bigintReplacer(_key: string, value: unknown) {
  return typeof value === "bigint" ? value.toString() : value;
}
function color_index_color(ci:number):string
{
  switch (ci)
  {
      case COLOR_INDEX_BLUE :  return "blue";
      case COLOR_INDEX_GREEN:  return "green";
      case COLOR_INDEX_RED:  return "red";
  }

   return "unknown color "+ci;
}

function get_blob_list(blobs:Uint32Array,blob_list:number[])
{
       var has_unknown_color = false;
       var color_str ="";

        for(var i = 0; i < 9 ; i++)
        {
           const bi = blob_list[i];
           const b  =  readBlob2At(blobs,bi);
           color_str+= color_index_color(b.color_index)+", ";
           if(b.color_index < 0)
           {
             has_unknown_color = true;
           }
        } 

        if(has_unknown_color) return "";
        return color_str;
}

 function get_sticker_spins(st:StickerInfoGpu)
 {
    var spin_str ="";
     for(var i = 0; i < 9 ; i++)
     {
       spin_str+= st.circle_spins[i]+", ";
     }
     return spin_str;
 }

 export function code_stats(words: Uint32Array,index:number,blobs:Uint32Array)
 {
     const st = readStickerAt(words, index);
     const code = st.code.toString();
     const blstr =   get_blob_list(blobs,st.blob_list);
     const spin_str = get_sticker_spins(st);
     var s ={code:code, debug_stage:st.debug_stage,blob_list:blstr,spins:spin_str};
     console.log(`BadCode[${index}]`, JSON.stringify(s,null, 2));

 }
export function printUsedStickers(words: Uint32Array,blobs:Uint32Array ,maxToPrint = 16): void {
  const indices = findUsedStickerIndices(words);

   var list = [];
    var highest_debug_stage = 0;

   for (const idx of indices.slice(0, maxToPrint))
   {
     const st = readStickerAt(words, idx);
     if(st.debug_stage >  3)
     {
        const code = st.code.toString();
        const blstr =   get_blob_list(blobs,st.blob_list);
        const spin_str = get_sticker_spins(st);
        if(blstr.length > 0) 
        {
          var s ={code:code, debug_stage:st.debug_stage,blob_list:blstr,spins:spin_str};
          list.push(s);

          if(st.debug_stage > highest_debug_stage)
          {
            highest_debug_stage = st.debug_stage;
          }
          
        }
        
     }
    
     console.log(`StickerInfoGpu[${highest_debug_stage}]`, JSON.stringify(list,null, 2));

  }
}

